import React, { useState, useEffect, useContext } from "react"
import { makeStyles } from "@mui/styles"
import api from "../../services/api"
import { AuthContext } from "../../context/Auth/AuthContext"

import { toast } from "react-toastify"
import { i18n } from "../../translate/i18n"
import { useHistory } from "react-router-dom"

import KanbanCardDescription from "../../components/KanbanCard"

import { ControlledBoard, moveCard } from "@caldwell619/react-kanban"
import "@caldwell619/react-kanban/dist/styles.css"
import {
    Box,
    FormControl,
    Grid2,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Typography,
} from "@mui/material"

import { useTheme } from "@mui/styles"

import { ColumnStyles } from "./styles"
import SelectColor from "./SelectColor"
import TagsContainer from "./TagsContainer"
import toastError from "../../errors/toastError"

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: theme.spacing(1),
    },
    button: {
        background: "#10a110",
        border: "none",
        padding: "8px",
        color: "white",
        fontWeight: "bold",
        borderRadius: "5px",
    },
    tagContainer: {
        display: "flex",
        gap: "0.3em",
        flexWrap: "wrap",
    },
    justifyBetween: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: "0.5em",
        marginBottom: "0.5em",
    },
}))

const Kanban = () => {
    const theme = useTheme()

    const classes = useStyles()
    const history = useHistory()
    const { user } = useContext(AuthContext)

    const [tags, setTags] = useState([])

    const [selectedQueues, setSelectedQueues] = useState([])
    const [limit, setLimit] = useState("all")

    const [users, setUsers] = useState([])
    const [selectedUser, setSelectedUser] = useState("all")

    const [loading, setLoading] = useState(true)

    const loadUsers = async () => {
        try {
            const { data } = await api.get(`/users`)

            setUsers(data.users)
        } catch (err) {
            toastError(err)
        }
    }

    useEffect(() => {
        async function fetchData() {
            await loadUsers()
        }
        fetchData()
    }, [])

    const fetchTags = async () => {
        try {
            const response = await api.get("/tags/kanban")
            const fetchedTags = response.data.lista || []

            setTags(fetchedTags)

            // Fetch tickets after fetching tags
            await fetchTickets(jsonString)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchTags()
    }, [selectedQueues, limit, selectedUser])

    const [board, setBoard] = useState({
        columns: [],
    })

    const [tickets, setTickets] = useState([])

    useEffect(() => {
        setSelectedQueues(user.queues)
    }, [user])

    const jsonString = user?.queues?.map((queue) => queue.UserQueue.queueId)

    const fetchTickets = async () => {
        try {
            setLoading(true)
            const queues = selectedQueues.map(
                (queue) => queue.UserQueue.queueId,
            )

            const params = {
                queueIds: JSON.stringify(queues),
            }

            if (selectedUser !== "all") {
                params.selectedUser = selectedUser
            }

            if (limit !== "all") {
                params.limit = limit
            }

            if (queues.length) {
                const { data } = await api.get("/ticket/kanban", {
                    params: params,
                })
                setTickets(data.tickets)
            } else {
                const { data } = await api.get("/ticket/kanban")
                setTickets(data.tickets)
            }
            setLoading(false)
        } catch (err) {
            setLoading(false)
            console.log(err)
            setTickets([])
        }
    }

    function filtrarPorTags(array) {
        return array.filter((item) => {
            // Verifica se a chave 'tags' não existe ou é um array vazio

            if (!item.hasOwnProperty("tags") || item.tags.length === 0) {
                return true
            }
            // Verifica se todas as tags possuem 'kanban: 0'
            return item.tags.every((tag) => tag?.kanban === 0)
        })
    }

    const popularCards = () => {
        const openTickets = filtrarPorTags(tickets)

        let defaultTagSum = 0
        if (openTickets.length) {
            openTickets.forEach((e) => {
                if (e.value) {
                    defaultTagSum += Number(e.value)
                } else if (e.products.length) {
                    defaultTagSum += e.products.reduce(
                        (sum, product) =>
                            sum +
                            product.price * product.TicketProduct.quantidade,
                        0,
                    )
                }
            })
        }

        const columns = [
            {
                id: "lane0",
                title: `${i18n.t("tagsKanban.laneDefault")} ${
                    openTickets?.length
                }`,
                label: defaultTagSum.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                }),
                cards: openTickets.map((ticket) => {
                    return {
                        id: ticket.id.toString(),
                        label: "Ticket nº " + ticket.id.toString(),
                        description: <KanbanCardDescription ticket={ticket} />,
                        title: ticket.contact.name,
                        draggable: true,
                        href: "/tickets/" + ticket.uuid,
                    }
                }),
                style: { backgroundColor: "transparent", color: "#333333" },
            },
            ...tags.map((tag) => {
                const filteredTickets = tickets.filter((ticket) => {
                    const tagIds = ticket.tags.map((tag) => tag.id)
                    return tagIds.includes(tag.id)
                })

                let tagSum = 0
                if (filteredTickets.length) {
                    filteredTickets.forEach((e) => {
                        if (e.value) {
                            tagSum += Number(e.value)
                        } else if (e.products.length) {
                            tagSum += e.products.reduce(
                                (sum, product) =>
                                    sum +
                                    product.price *
                                        product.TicketProduct.quantidade,
                                0,
                            )
                        }
                    })
                }

                return {
                    id: tag.id.toString(),
                    title: `${tag.name} ${filteredTickets?.length}`,
                    label: tagSum.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    }),
                    cards: filteredTickets.map((ticket) => ({
                        id: ticket.id.toString(),
                        label: "Ticket nº " + ticket.id.toString(),
                        description: <KanbanCardDescription ticket={ticket} />,
                        title: ticket.contact.name,
                        draggable: true,
                        href: "/tickets/" + ticket.uuid,
                    })),
                    style: { backgroundColor: tag.color, color: "white" },
                }
            }),
        ]

        setBoard({ columns })
    }

    const handleCardClick = (uuid) => {
        history.push("/tickets/" + uuid)
    }

    const onCardClick = (cardId) => {
        const card = tickets.filter((e) => e.id === cardId)[0]
        handleCardClick(card.uuid)
    }

    useEffect(() => {
        popularCards(jsonString)
    }, [tags, tickets])

    const handleCardMove = async (fromLaneId, toLaneId, ticketId) => {
        try {
            const arr = tickets
            const i = arr
                .map((item) => Number(item.id))
                .indexOf(Number(ticketId))
            arr[i].tags = arr[i].tags?.filter(
                (item) => Number(item.id) !== Number(fromLaneId),
            )

            await api.delete(`/ticket-tags/${ticketId}`)
            if (toLaneId !== "lane0") {
                arr[i].tags = [
                    ...arr[i].tags,
                    ...tags.filter(
                        (item) => Number(item.id) === Number(toLaneId),
                    ),
                ]
                await api.put(`/ticket-tags/${ticketId}/${toLaneId}`)
            }

            setTickets(arr)
            popularCards()
        } catch (err) {
            toast.error(
                "Ocorreu um erro ao tentar mover o ticket. Atualize a página.",
            )
            console.log(err)
        }
    }

    const handleCardMove2 = (_card, source, destination) => {
        handleCardMove(source.fromColumnId, destination.toColumnId, _card.id)
        setBoard((currentBoard) => {
            return moveCard(currentBoard, source, destination)
        })
    }

    const handleCreateNewLane = async (name) => {
        const tagData = {
            name: name,
            color: "#12273c",
            kanban: 1,
            userId: user.id,
        }
        await api.post("/tags", tagData)
        fetchTags()
    }

    const updateLane = async ({ id, color, name }) => {
        const tagData = {
            name: name,
            color: color,
            kanban: 1,
            userId: user.id,
        }
        await api.put(`/tags/${id}`, tagData)
        fetchTags()
    }

    if (loading) {
        return (
            <div className={classes.root}>
                <Skeleton variant="rounded" width={600} height={55} />
                <Box display="flex" gap={2} mt={2}>
                    <Skeleton variant="rounded" width={210} height={420} />
                    <Skeleton variant="rounded" width={210} height={340} />
                    <Skeleton variant="rounded" width={210} height={420} />
                    <Skeleton variant="rounded" width={210} height={340} />
                </Box>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            {user.profile === "admin" && (
                <Box display="flex" gap={1} width="100%" mt={2}>
                    <TagsContainer
                        queues={user.queues}
                        selecteds={selectedQueues}
                        setSelecteds={setSelectedQueues}
                    />

                    <FormControl fullWidth sx={{ maxWidth: 200 }}>
                        <InputLabel id="limit-cards">Usuário</InputLabel>
                        <Select
                            labelId="limit-cards"
                            label="Usuário"
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            size="small"
                        >
                            <MenuItem value={"all"}>Todos</MenuItem>
                            {users?.map((user) => (
                                <MenuItem key={user?.id} value={user?.id}>
                                    {user?.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ maxWidth: 200 }}>
                        <InputLabel id="limit-cards">
                            Limite de resultados
                        </InputLabel>
                        <Select
                            labelId="limit-cards"
                            label="Limite de resultados"
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            size="small"
                        >
                            <MenuItem value={200}>Últimos 200</MenuItem>
                            <MenuItem value={400}>Últimos 400</MenuItem>
                            <MenuItem value={800}>Últimos 800</MenuItem>
                            <MenuItem value={"all"}>Trazer tudo</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            )}
            <ColumnStyles backgroundColor={theme.palette.tabHeaderBackground}>
                <ControlledBoard
                    onCardDragEnd={handleCardMove2}
                    allowAddColumn
                    disableColumnDrag
                    onNewColumnConfirm={({ title }) =>
                        handleCreateNewLane(title)
                    }
                    allowRenameColumn
                    renderColumnHeader={(lane) => {
                        return (
                            <Grid2
                                display="flex"
                                justifyContent="space-between"
                                sx={{
                                    marginBottom: 2,
                                }}
                            >
                                <Grid2
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    position="relative"
                                >
                                    {lane.id !== "lane0" && (
                                        <SelectColor
                                            currentColor={
                                                lane.style?.backgroundColor
                                            }
                                            onSelectColor={(color) =>
                                                updateLane({
                                                    id: lane.id,
                                                    color,
                                                })
                                            }
                                        />
                                    )}
                                    <Typography variant="body1">
                                        {lane.title}
                                    </Typography>
                                </Grid2>
                                <Typography variant="body1">
                                    {lane.label}
                                </Typography>
                            </Grid2>
                        )
                    }}
                    renderCard={(card) => (
                        <KanbanCardDescription
                            ticket={card?.description.props.ticket}
                            title={card?.title}
                            onCardClick={() => onCardClick(Number(card.id))}
                        />
                    )}
                    allowAddCard={false}
                >
                    {board}
                </ControlledBoard>
            </ColumnStyles>
        </div>
    )
}

export default Kanban
